import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CommentItem.module.css';
import { Comment } from './CommentsPopup';
import ContextMenu from '../ContextMenu/ContextMenu';
import { ContextMenuItem } from '../../../Types/ContextMenuItem';
import { getDeleteContextMenuItem } from '../../../Utils/ContextMenuStyles';
import DropDownList from '../DropDownList/DropDownList';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import CommentDisplayCard from './CommentDisplayCard';

type CommentItemProps = {
    comment: Comment;
    onEdit?: (id: string, text: string, status?: string) => void;
    onDelete?: (id: string) => void;
    canEdit?: boolean;
    canDelete?: boolean;
    statusOptions?: IDropDownListItem[];
    /** When set, replaces the default "delete comment" confirmation copy. */
    deleteConfirmation?: { title: string; message: string };
    /** When edit/delete are disabled, tooltip on ⋯ (e.g. KPI gated by role or ownership). */
    readOnlyMenuTooltip?: string;
    /**
     * KPI progress notes: parent card shows date, owner, and status; this item is body text only.
     * Pair with `menuMountEl` to render the ⋯ control in the parent (e.g. note header).
     */
    metadataShownElsewhere?: boolean;
    /** DOM node to portal the context menu into when `metadataShownElsewhere` is true. */
    menuMountEl?: HTMLElement | null;
    /**
     * With `metadataShownElsewhere`: hide the grey body when there is no comment text until the user
     * opens Edit (KPI progress notes — avoids an empty box for the current user's text-less notes).
     */
    omitEmptyKpiBody?: boolean;
    /** Notified when inline edit mode toggles (KPI card uses this to show the body section only while editing). */
    onEditingChange?: (editing: boolean) => void;
};

const CommentItem = ({
    comment,
    onEdit,
    onDelete,
    canEdit = true,
    canDelete = true,
    statusOptions,
    deleteConfirmation,
    readOnlyMenuTooltip,
    metadataShownElsewhere = false,
    menuMountEl = null,
    omitEmptyKpiBody = false,
    onEditingChange,
}: CommentItemProps) => {
    const [isEditing, setIsEditing] = useState(false);

    const setEditing = useCallback(
        (next: boolean) => {
            setIsEditing(next);
            onEditingChange?.(next);
        },
        [onEditingChange],
    );
    const [editText, setEditText] = useState(comment.CommentText);
    const [selectedStatus, setSelectedStatus] = useState<string | undefined>(comment.ProgressStatus);

    useEffect(() => {
        if (!isEditing) {
            setEditText(comment.CommentText);
            setSelectedStatus(comment.ProgressStatus);
        }
    }, [
        comment.CommentText,
        comment.ProgressStatus,
        comment.id,
        isEditing,
    ]);

    const handleSave = () => {
        if (onEdit) {
            onEdit(comment.id, editText, selectedStatus);
        }
        setEditing(false);
    };

    const handleCancel = () => {
        setEditText(comment.CommentText);
        setEditing(false);
    };

    const contextMenuItems: ContextMenuItem[] = [
        {
            title: 'Edit',
            icon: '/img/edit.svg',
            onClickHandler: () => setEditing(true),
            disabled: !canEdit || !comment.Editable,
            requiresConfirmation: false
        },
        {
            ...getDeleteContextMenuItem(
                'Delete',
                () => onDelete?.(comment.id),
                true,
                deleteConfirmation ?? {
                    title: 'Delete Comment',
                    message: 'Are you sure you want to delete this comment? This action cannot be undone.',
                    severity: 'danger'
                }
            ),
            disabled: !canDelete || !comment.Editable
        }
    ];

    const menuControl = (
        <ContextMenu
            items={contextMenuItems}
            contextId={comment.id}
            position="left"
            disabledTooltip={
                comment.Editable
                    ? undefined
                    : (readOnlyMenuTooltip ?? "You cannot edit or delete this comment")
            }
        >
            <button type="button" className={styles.commentMenu}>⋯</button>
        </ContextMenu>
    );

    const editButtons = (
        <>
            <button type="button" className={styles.saveButton} onClick={handleSave}>
                Save
            </button>
            <button type="button" className={styles.cancelButton} onClick={handleCancel}>
                Cancel
            </button>
        </>
    );

    const trailing = isEditing ? (metadataShownElsewhere ? undefined : editButtons) : menuControl;

    const editBody = (
        <>
            <textarea
                className={styles.editTextarea}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
            />
            {statusOptions && (
                <DropDownList
                    data={statusOptions}
                    value={selectedStatus || ""}
                    placeholder={''}
                    onChange={(value) => setSelectedStatus(value.value)}
                />
            )}
            {metadataShownElsewhere && (
                <div className={styles.editActionsRow}>{editButtons}</div>
            )}
        </>
    );

    const readBody = (
        <div className={styles.commentText}>
            {comment.CommentText}
        </div>
    );

    if (metadataShownElsewhere) {
        const trimmed = (comment.CommentText ?? "").trim();
        const hideEmptyBody =
            omitEmptyKpiBody && !isEditing && trimmed.length === 0;

        return (
            <>
                {menuMountEl && !isEditing ? createPortal(menuControl, menuMountEl) : null}
                {!hideEmptyBody && (
                    <CommentDisplayCard
                        commenter={comment.Commenter}
                        dateMade={comment.DateMade}
                        progressStatus={comment.ProgressStatus}
                        showMetadataRow={false}
                    >
                        {isEditing ? editBody : readBody}
                    </CommentDisplayCard>
                )}
            </>
        );
    }

    return (
        <CommentDisplayCard
            commenter={comment.Commenter}
            dateMade={comment.DateMade}
            progressStatus={comment.ProgressStatus}
            trailing={trailing}
        >
            {isEditing ? editBody : readBody}
        </CommentDisplayCard>
    );
};

export default CommentItem;
